/**
 * Listing locations come from owner-entered data, so the same city arrives in many shapes:
 * "SYRACUSE", "salt lake city", "SLC", "ST GEORGE". This normalizes a city name for display.
 *
 * Three fixes, in order:
 *  1. Known whole-name abbreviations expand to the full city ("SLC" -> "Salt Lake City").
 *  2. Abbreviated leading words expand ("FT WORTH" -> "Fort Worth", "N LAS VEGAS" -> "North Las Vegas").
 *  3. Names that are entirely upper- or lower-case are re-cased as a title ("SYRACUSE" -> "Syracuse").
 *
 * Mixed-case input is left alone on purpose — an owner who typed "McCall" or "LaSalle" already
 * got it right, and title-casing would only break it.
 */

/**
 * Whole-name abbreviations common enough in listing data to be unambiguous. Keys are matched
 * after stripping periods and spaces and upper-casing, so "S.L.C." and "SLC" both hit.
 *
 * Deliberately short: anything that could plausibly mean two places (e.g. "LA", or "WA" as a
 * city) stays out, since expanding to the wrong city is worse than not expanding at all.
 */
const CITY_ABBREVIATIONS: Record<string, string> = {
  SLC: 'Salt Lake City',
  NYC: 'New York',
  OKC: 'Oklahoma City',
  KC: 'Kansas City',
  CDA: "Coeur d'Alene",
  VEGAS: 'Las Vegas',
};

/**
 * Abbreviated words that appear inside city names. Applied per-word so they generalize to any
 * city rather than needing one entry per "Fort …"/"Mount …"/"St. …" town.
 */
const WORD_ABBREVIATIONS: Record<string, string> = {
  ft: 'Fort',
  mt: 'Mount',
  mtn: 'Mountain',
  st: 'St.',
  ste: 'Ste.',
};

/**
 * Directionals, expanded only when they lead the name — "N Las Vegas" is North Las Vegas, but a
 * lone "W" further in is more likely an initial than a direction.
 */
const LEADING_ABBREVIATIONS: Record<string, string> = {
  n: 'North',
  no: 'North',
  s: 'South',
  so: 'South',
  e: 'East',
  w: 'West',
};

/** Words that stay lower-case inside a name, but never as the first or last word. */
const MINOR_WORDS = new Set([
  'a',
  'an',
  'and',
  'at',
  'by',
  'de',
  'del',
  'des',
  'du',
  'en',
  'for',
  'in',
  'le',
  'les',
  'of',
  'on',
  'or',
  'sur',
  'the',
  'upon',
  'von',
]);

/** Names that the generic rules get wrong, keyed by their lower-cased, punctuation-free form. */
const CASING_EXCEPTIONS: Record<string, string> = {
  'coeur dalene': "Coeur d'Alene",
  'washington dc': 'Washington D.C.',
};

/** `Mc`/`Mac`/`O'` prefixes keep the following letter capitalized: "mcallen" -> "McAllen". */
const capitalizeGaelicPrefix = (word: string): string =>
  word.replace(
    /^(mc|o['’])(\p{L})/iu,
    (_, prefix: string, letter: string) =>
      `${prefix.charAt(0).toLocaleUpperCase()}${prefix.slice(1)}${letter.toLocaleUpperCase()}`,
  );

/** Title-cases one word, recursing through hyphens and slashes so "winston-salem" works. */
const titleCaseWord = (word: string, isEdgeWord: boolean): string => {
  if (/[-/]/.test(word)) {
    return word
      .split(/([-/])/)
      .map(part => (/^[-/]$/.test(part) ? part : titleCaseWord(part, true)))
      .join('');
  }

  const lower = word.toLocaleLowerCase();

  // Nothing to case (ordinals like "3rd", stray punctuation).
  if (!/\p{L}/u.test(lower)) {
    return lower;
  }

  if (!isEdgeWord && MINOR_WORDS.has(lower)) {
    return lower;
  }

  return capitalizeGaelicPrefix(lower.replace(/\p{L}/u, letter => letter.toLocaleUpperCase()));
};

/** Expands an abbreviated word; directionals only count when they lead the name. */
const expandWord = (word: string, index: number): string => {
  const key = word.toLocaleLowerCase().replace(/\.$/, '');
  if (index === 0 && LEADING_ABBREVIATIONS[key]) {
    return LEADING_ABBREVIATIONS[key];
  }
  return WORD_ABBREVIATIONS[key] ?? word;
};

const toTitleCase = (value: string): string => {
  const words = value.split(' ');
  const lastIndex = words.length - 1;

  return words
    .map((word, index) => {
      const expanded = expandWord(word, index);
      // An expansion is already correctly cased; don't re-case "St." into "St.".
      if (expanded !== word) {
        return expanded;
      }
      return titleCaseWord(word, index === 0 || index === lastIndex);
    })
    .join(' ');
};

/** True when the value carries no case information of its own (ALL CAPS or all lowercase). */
const hasUniformCasing = (value: string): boolean => {
  const letters = value.replace(/[^\p{L}]/gu, '');
  if (!letters) {
    return false;
  }
  return letters === letters.toLocaleUpperCase() || letters === letters.toLocaleLowerCase();
};

/**
 * Normalizes a listing's city name for display.
 *
 * Returns the input unchanged when it is already mixed-case and not a known abbreviation, so
 * correctly-entered names ("McCall", "DeLand") survive untouched.
 */
export const formatCityName = (city?: string | null): string => {
  if (!city) {
    return '';
  }

  const trimmed = city.trim().replace(/\s+/g, ' ');

  if (!trimmed) {
    return '';
  }

  const abbreviation = CITY_ABBREVIATIONS[trimmed.replace(/[.\s]/g, '').toUpperCase()];
  if (abbreviation) {
    return abbreviation;
  }

  const exception = CASING_EXCEPTIONS[trimmed.toLocaleLowerCase().replace(/['’.]/g, '')];
  if (exception) {
    return exception;
  }

  if (!hasUniformCasing(trimmed)) {
    return trimmed;
  }

  return toTitleCase(trimmed);
};

/**
 * Formats "{city}, {region}" for a listing, normalizing the city and dropping empty parts.
 * Region is a state/province/county code that the API already returns standardized.
 */
export const formatCityRegion = (city?: string | null, region?: string | null): string =>
  [formatCityName(city), region?.trim()].filter(Boolean).join(', ');
